import React from "react";

import { InfoSmIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import Error from "Components/Error";
import Button from "ds/Button";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import * as InputAll from "ds/Input";
import { Tooltip } from "ds/Tooltip";
import { capitalize } from "Libs/utils";

import {
  ListFieldLayout,
  InputsWrapper,
  InputWrapper,
  InputLayout,
  IconControl,
  RemoveButton,
  LabelWrapper,
  InputLabel
} from "./LoginListField.styles";

import type {
  LoginDefault,
  LoginFormTypes
} from "src/organization/pages/project/environment/settings/general/containers/EnvironmentGeneralSettings/types";

type LoginListFieldProps = {
  onLoginsChangeHandler: (
    newValue: string,
    field: LoginFormTypes,
    index: number
  ) => void;
  onLoginAddHandler: () => void;
  removeLineHandler: (index: number) => void;
  basicAuth: LoginDefault[];
  enabled: boolean;
  errors?: object;
  checkPasswordStrength: boolean;
  checkLoginsValidation: (field: LoginFormTypes, index: number) => void;
};

const LoginListField = ({
  onLoginsChangeHandler,
  onLoginAddHandler,
  removeLineHandler,
  basicAuth,
  enabled,
  errors,
  checkPasswordStrength,
  checkLoginsValidation
}: LoginListFieldProps) => {
  const intl = useIntl();

  const fieldErrors = {};
  let generalError = "";

  if (errors) {
    Object.keys(errors).forEach(errField => {
      const res = errField.match(/^http_access\.basic_auth\.(.*)/);

      if (res) {
        if (res[1] === "key") {
          generalError = errors[errField];
        } else {
          const log = basicAuth.find(log => log.login.value === res[1]);
          let line: number | undefined = undefined;
          if (log) {
            line = basicAuth.indexOf(log);
            generalError = "";

            fieldErrors[line] = { password: errors[errField] };
          }
        }
      }
    });
  }

  const addLoginEl = (
    <Button
      id="login-list-field-add"
      onClick={onLoginAddHandler}
      disabled={!enabled}
      analyticId="add_login"
    >
      {capitalize(intl.formatMessage({ id: "add_login" }))}
    </Button>
  );

  const addLoginComponent = enabled ? (
    addLoginEl
  ) : (
    <Tooltip
      tooltip={intl.formatMessage({
        id: "admin_change"
      })}
      analyticId="admin_change"
    >
      {addLoginEl}
    </Tooltip>
  );

  return (
    <ListFieldLayout>
      {generalError && <Error>{generalError}</Error>}
      {basicAuth.map(({ login, password }, index) => {
        const showLoginError = login.isTouched && !login.isValid;
        const showPasswordError = password.isTouched && !password.isValid;

        return (
          <InputsWrapper key={`line-${index}`}>
            <InputLayout>
              <InputWrapper>
                <Input
                  analyticId="login_label"
                  id={`login-${index}`}
                  name={`login-${index}`}
                  onChange={e =>
                    onLoginsChangeHandler(e.currentTarget.value, "login", index)
                  }
                  value={login.value}
                  disabled={!enabled}
                  aria-disabled={!enabled}
                  label={intl.formatMessage({ id: "login_label" })}
                  placeholder={intl.formatMessage({ id: "login_label" })}
                  isValid={!showLoginError}
                  error={
                    showLoginError &&
                    intl.formatMessage({
                      id: "login_length-error"
                    })
                  }
                  required
                  width="264px"
                  // onBlur={() => (true)}
                  onBlur={() => checkLoginsValidation("login", index)}
                />
              </InputWrapper>
            </InputLayout>

            <InputLayout>
              <InputWrapper>
                <InputAll.Root>
                  <LabelWrapper>
                    <InputLabel
                      htmlFor={`password-${index}`}
                      isValid={!showPasswordError}
                      required
                    >
                      {intl.formatMessage({ id: "password_label" })}
                    </InputLabel>
                    {checkPasswordStrength ? (
                      <Dialog
                        description={intl.formatMessage({
                          id: "password_length"
                        })}
                        analyticId="text"
                        align="center"
                        trigger={
                          <IconControl aria-label="info-trigger-password">
                            <InfoSmIcon />
                          </IconControl>
                        }
                      />
                    ) : null}
                  </LabelWrapper>
                  <InputAll.Input
                    id={`password-${index}`}
                    name={`password-${index}`}
                    onChange={e =>
                      onLoginsChangeHandler(
                        e.currentTarget.value,
                        "password",
                        index
                      )
                    }
                    value={password.value}
                    disabled={!enabled}
                    aria-disabled={!enabled}
                    placeholder={intl.formatMessage({
                      id: "password_label"
                    })}
                    isValid={!showPasswordError}
                    required
                    width="264px"
                    onBlur={() => checkLoginsValidation("password", index)}
                  />
                  {showPasswordError && (
                    <InputAll.Error>
                      {intl.formatMessage({
                        id: "password_length-error"
                      })}
                    </InputAll.Error>
                  )}
                </InputAll.Root>

                {enabled && (
                  <RemoveButton
                    variant="link"
                    analyticId="remove"
                    id={`login-list-field-add-delete-${index}`}
                    aria-label={intl.formatMessage({ id: "remove" })}
                    onClick={() => removeLineHandler(index)}
                  >
                    {intl.formatMessage({ id: "remove" })}
                  </RemoveButton>
                )}
              </InputWrapper>
            </InputLayout>
          </InputsWrapper>
        );
      })}
      <div>{addLoginComponent}</div>
    </ListFieldLayout>
  );
};

export default LoginListField;
